/* eslint-disable camelcase */
/* eslint-disable no-unused-vars */
/** @module Auth0IdentityProviderShopify */
/* eslint-disable import/extensions */
import Auth0IdentityProvider from './auth0-identity-provider.mjs';
import Auth0CookieHelper from './auth0-cookie-helper.mjs';
import Auth0Constants from './auth0-constants.mjs';
import Auth0UtilsBrowser from './auth0-utils-browser.mjs';
import logger from './logging/auth0-logger.mjs';

/**
 * NOTES
 * This IDP is currently unreleased because it is seen as not valuable enough to incur the complexity of a self-hosted oauth2 proxy
 * required in order to make it work (so it's useful beyond the scope of just a single shopify store)
 *
 * If we decide to finish this work (almost all work is complete):
 * - A new Shopify app must be created. This app should be public, but unlisted so as not to confuse with the other ShipStation app
 * - Maybe call it ShipStation OAUTH or something
 * - This new Shopify app must call upon a specific route that is currently in the SSAS (can be anywhere). Must call `/oauth2proxy/idpsignin` route
 * - The rest of the /oauth2proxy endpoints currently SSAS must also be hosted somewhere
 * - The custom social IDP named "shopify-oauth2" must point to wherever those endpoints are hosted
 * - The idp class has an overridable function named runNewUserOnboardingActions(). This mut be implemented for Shopify such that it does the work to auto connect the primary shopify store
 * - That function is not currently called anywhere. Do a search on it and you'll find a TODO where it should be placed.
 *
 * Dev Notes (to get Shopify SSO running on localhost)
 * - Normal caddy server startup required
 * - Currently using the existing ShipStation shopify app because you can't install draft apps. Until there is a published Shopify app for oauth, you'll need to alter your etc/hosts file
 *   to temporarily hijack that app's install / load callback. hosts entry is "127.0.0.1     ss6.shipstation.com"
 * - Also need an entry for the SSAS service => "127.0.0.1     local-ssas.shipstation.com"
 * - Until the SSAS endpoints are hosted somewhere public, you'll need to convince the Auth0 custom idp named "shopify-oauth2" to point to localhost in order to fetch a new token
 *   Using ngrok for this:
 *     - Start ngrok as "ngrok http 4000"
 *     - In the Auth0 config for this idp, point the token URL to ngrok (e.g. https://1e39-152-117-114-175.ngrok-free.app/oauth2proxy/token)
 */

/**
 * @constructor
 * @description A Shopify-specific implementation of Auth0IdentityProvider
 */
class Auth0IdentityProviderShopify extends Auth0IdentityProvider {
  constructor(idpData) {
    super(idpData);
    this._shopifyAppUrl = idpData.shopifyAppUrl;
  }

  /**
   * @inheritdoc
   */
  beforeAuthRedirect(loginWithRedirectOptions) {
    console.log('SHOPIFY before login redirect...');
    super.beforeAuthRedirect(loginWithRedirectOptions);
    const qs = Auth0UtilsBrowser.parseQueryString();
    const shopName = qs.get('shop') || this._getUserShopifyShopHostRecord();

    if (!shopName) {
      this.reset();
      throw new Error('Auth0IdentityProviderShopify.beforeAuthRedirect request, but current URL does not contain shopify host name');
    }

    console.log(`start shopify SSO app install or open request for "${shopName}"`);
    // in the case this machine is shared or there are multiple shopify accounts involved, delete the record every time and only re-write it again
    // after successful shopify auth. In this way, after the first failure with the "wrong" shopify store, the SSO flow will reset and user will
    // re-identify their store. We can't fully delete it because it needs to go to the server tho, so write it to in-progress holder
    this._removeShopifyShopHostRecord();
    this._writeUserInProgressShopifyShopHostRecord(shopName);
  }

  /**
   * @inheritdoc
   */
  afterAuthRedirect(userLoginData, auth0Client) {
    console.log('SHOPIFY after login redirect...');
    super.afterAuthRedirect(userLoginData, auth0Client);
    // see beforeAuthRedirect() context here. After successful auth, we remove the in progress cookie and replace it with the actual shop name.
    // In this way, any error is implied by NOT executing this function (we do not get callbacks for shopify auth errors so we need to set an error
    // state in beforeAuthRedirect and cancel it if successful)
    const shopName = this._getUserInProgressShopifyShopHostRecord();
    if (!shopName) {
      // can this happen? In any case, not an error, will just force shopify app store on next login
      logger.warn('Auth0IdentityProviderShopify.afterAuthRedirect was unable to find the in progress shop in order to restore record.');
      return;
    }

    console.log(`completed shopify SSO signin for "${shopName}"`);
    this._writeUserShopifyShopHostRecord(shopName);
  }

  /**
   * @inheritdoc
   */
  signinWithRedirectHandler(successCallbackUrl) {
    console.log(`Auth0IdentityProviderShopify.signinWithRedirectHandler override for ${this.id} starts...`);

    const shopifyHost = this._getUserShopifyShopHostRecord();
    if (shopifyHost) {
      // ok to start normal oauth2 flow, 'shopify_host' cookie will ship off to ss oauth2 proxy service
      super.signinWithRedirectHandler(successCallbackUrl);
      return;
    }

    console.log('Start shopify SSO with no known shop name...');
    // set the idp login with redirect cookie so we have context when we make is back here after install (or open)
    this._setLoginWithRedirectContextForThisIdp();
    this._setIdpContextForThisIdp();
    this._redirectToShopifyAppStore();
  }

  /**
   * @inheritdoc
   */
  isSigninActive(eventOptions) {
    if (super.isSigninActive(eventOptions)) return true;

    const qs = Auth0UtilsBrowser.isBrowser
      ? Auth0UtilsBrowser.parseQueryString()
      : new URLSearchParams(eventOptions.req.query);

    console.log(`isSigninActive for Shopify: qs => ${qs.toString()}`);
    // otherwise, we may have started signin from shopify's app store which has some distinct characteristics
    // there are other vals here that can be used if further distinguishing is needed
    // See step 1 here: https://shopify.dev/docs/apps/auth/get-access-tokens/authorization-code-grant/getting-started
    return qs.get('hmac') && qs.get('shop');
  }

  get shopifyAppUrl() {
    return this._shopifyAppUrl;
  }

  /**
   * @inheritdoc
   */
  async signinWithPopupHandler(successCallbackUrl) {
    throw new Error('The Shopify IDP login page is not compatible with browser popup windows, use redirect instead.');
  }

  /**
   * @inheritdoc
   */
  idpInitiatedSignin(eventOptions) {
    console.log(`idpInitiatedSignin for ${this.id}...`);

    this._validateShopifyHmac({ queryString: eventOptions.queryString });

    // need to mimic state set when button is clicked => the next function we'll go thru for Shopify is beforeAuthRedirect() on the browser, so make sure that won't fail
    // NOTE super.idpInitiatedSignin takes care of those 2 cookies (for ALL IDPs)
    const redirectData = super.idpInitiatedSignin(eventOptions);
    // now add shopify specific data
    redirectData.queryString.shop = eventOptions.queryString.shop;
    return redirectData;
  }

  /**
   * @inheritdoc
   */
  selectName(oauthUserProfile) {
    const { shop_owner } = oauthUserProfile;
    if (shop_owner) return shop_owner;

    return super.selectName(oauthUserProfile);
  }

  /**
   * @inheritdoc
   */
  selectRequestedLanguage(oauthUserProfile) {
    const { primary_locale } = oauthUserProfile;
    if (primary_locale) return primary_locale;

    return super.selectRequestedLanguage(oauthUserProfile);
  }

  /**
   * @inheritdoc
   */
  selectPhoneNumber(oauthUserProfile) {
    const { phone } = oauthUserProfile;
    if (phone) return phone;

    return super.selectPhoneNumber(oauthUserProfile);
  }

  /**
   * @inheritdoc
   */
  selectCountryCode(oauthUserProfile) {
    const { country_code } = oauthUserProfile;
    if (country_code) return country_code;
    const { country } = oauthUserProfile;
    if (country) return country;

    return super.selectCountryCode(oauthUserProfile);
  }

  // OAUTH2 PROXY ++++++++++++
  /**
   * @inheritdoc
   */
  oauth2ProxyAuthorize(eventOptions) {
    console.log(`oauth2ProxyAuthorize for ${this.id}...`);

    const shopifyHost = eventOptions.cookies[Auth0Constants.SHOPIFY_SHOP_NAME_IN_PROGRESS_KEY];
    if (!shopifyHost) {
      throw new Error('Shopify oauth2 has failed because the Shopify shop context is not available (authorize).');
    }
    console.log(`FOUND SHOPIFY HOST COOKIE => ${shopifyHost}`);
    // NOTE: if we need to store a long lived Shopify token for some reason, omit "grant_options[]=per-user" from the custom params below. This will produce an offline token and ALSO remove the "associated_user" from ctx. We will need to call the shopify api manually to get the user profile in that case.
    return {
      idpAuthorizeUrl: `https://${shopifyHost}/oauth2proxy/authorize`,
      idpAuthorizeCustomParams: {
        'grant_options[]': 'per-user',
      },
    };
  }

  /**
   * @inheritdoc
   */
  oauth2ProxyAuthorizeCallback(eventOptions) {
    console.log(`oauth2ProxyAuthorizeCallback for ${this.id}...`);

    this._validateShopifyHmac({ queryString: eventOptions.queryString });

    if (!eventOptions.queryString?.shop) {
      throw new Error('Shopify oauth2 has failed because the Shopify shop context is not available (authorize callback).');
    }
    return eventOptions.queryString.shop;
  }

  /**
   * @inheritdoc
   */
  oauth2ProxyToken(eventOptions) {
    console.log(`oauth2ProxyToken for ${this.id} with context => ${JSON.stringify(eventOptions.authorizeContext)}...`);
    const shopifyHost = eventOptions.authorizeContext;
    if (!shopifyHost) {
      throw new Error('Shopify oauth2 has failed because the Shopify shop context is not available (token).');
    }
    console.log(`oauth2ProxyToken shop context ${shopifyHost}...`);
    return {
      idpTokenUrl: `https://${shopifyHost}/oauth2proxy/access_token`,
    };
  }

  /**
   * @inheritdoc
   */
  async oauth2ProxyUserProfile(eventOptions) {
    console.log(`oauth2ProxyUserProfile for ${this.id}...`);
    console.log(`   with auth context ${JSON.stringify(eventOptions.authorizeContext)}...`);
    console.log(`   and token results ${JSON.stringify(eventOptions.tokenResponse)}...`);

    const shopifyHost = eventOptions.authorizeContext;
    if (!shopifyHost) {
      throw new Error('Shopify oauth2 has failed because the Shopify shop context is not available (user profile).');
    }
    console.log(`oauth2ProxyUserProfile shop context ${shopifyHost}...`);

    const data = await fetch(`https://${shopifyHost}/admin/api/2024-04/shop.json`, {
      method: 'get',
      headers: {
        'X-Shopify-Access-Token': eventOptions.tokenResponse.access_token,
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      },
    });

    if (!data.ok) {
      throw new Error(`Shopify oauth2 has failed because the Shopify shop API returned an error (user profile). ${data.status}`);
    }
    const shopData = await data.json();

    // use the shopify token response as the base object
    const userProfile = {
      ...eventOptions.tokenResponse,
      shop: shopData.shop,
    };

    userProfile.associated_user.shop_domain = shopifyHost;
    return userProfile;
  }
  // End OAUTH2 Proxy ++++++++++++++

  /**
   * @inheritdoc
   */
  reset() {
    this._removeShopifyShopHostRecord();
    this._removeInProgressShopifyShopHostRecord();
    super.reset();
  }

  _redirectToShopifyAppStore = () => {
    window.location.href = this.shopifyAppUrl;
  };

  _getUserShopifyShopHostRecord = () => {
    const shopName = Auth0CookieHelper.getCookieValue(Auth0Constants.SHOPIFY_SHOP_HOST_NAME_KEY);
    console.log(`Auth0IdentityProviderShopify._getUserShopifyShopHostRecord() => ${shopName}`);
    return shopName;
  };

  _getUserInProgressShopifyShopHostRecord = () => {
    const shopName = Auth0CookieHelper.getCookieValue(Auth0Constants.SHOPIFY_SHOP_NAME_IN_PROGRESS_KEY);
    console.log(`IN PROGRESS => Auth0IdentityProviderShopify._getUserInProgressShopifyShopHostRecord() => ${shopName}`);
    return shopName;
  };

  _writeUserShopifyShopHostRecord = (shopName) => {
    console.log(`Auth0IdentityProviderShopify._writeUserShopifyShopHostRecord() => wrote ${shopName}`);
    Auth0CookieHelper.setCookieValueWithExpirationInDays(Auth0Constants.SHOPIFY_SHOP_HOST_NAME_KEY, shopName, 365);
  };

  _writeUserInProgressShopifyShopHostRecord = (shopName) => {
    console.log(`IN PROGRESS => Auth0IdentityProviderShopify._writeUserInProgressShopifyShopHostRecord() => wrote ${shopName}`);
    Auth0CookieHelper.setCookieValueWithExpirationInDays(Auth0Constants.SHOPIFY_SHOP_NAME_IN_PROGRESS_KEY, shopName, 365);
  };

  _removeShopifyShopHostRecord = () => {
    console.log('Auth0IdentityProviderShopify._removeShopifyShopHostRecord()');
    Auth0CookieHelper.deleteCookie(Auth0Constants.SHOPIFY_SHOP_HOST_NAME_KEY);
  };

  _removeInProgressShopifyShopHostRecord = () => {
    console.log('IN PROGRESS => Auth0IdentityProviderShopify._removeInProgressShopifyShopHostRecord()');
    Auth0CookieHelper.deleteCookie(Auth0Constants.SHOPIFY_SHOP_NAME_IN_PROGRESS_KEY);
  };

  /**
   * @function
   * @instance
   * @private
   * @memberof Auth0IdentityProviderShopify
   * @description For use in NodeJS only. All requests coming from Shopify should be verified as authentic, See step 1 here: {@link https://auth0.com/docs/api/management/v2/introduction |https://auth0.com/docs/api/management/v2/introduction}. This function throws is the request is invalid.
   * @param {object} hmacData Data required in order to validate a shopify request
   * @param {object} eventOptions.queryString The querystring object that accompanied the Shopify request
   */
  _validateShopifyHmac = async (hmacData) => {
    const { hmac: hmacVal } = hmacData.queryString;
    if (!hmacVal) {
      throw new Error('Failed to validate Shopify request (1)');
    }
    const qs = new URLSearchParams(hmacData.queryString);
    qs.delete('hmac');
    const newQueryString = qs.toString();

    const crypto = await import('crypto');
    const hmac = crypto.createHmac('sha256', process.env.SHOPIFY_APP_SECRET);
    hmac.update(newQueryString);
    const digest = hmac.digest('hex');

    // Secure compare function to avoid timing attacks
    function secureCompare(a, b) {
      if (a.length !== b.length) {
        return false;
      }
      let result = 0;
      for (let i = 0; i < a.length; i++) {
        // eslint-disable-next-line no-bitwise
        result |= a.charCodeAt(i) ^ b.charCodeAt(i);
      }
      return result === 0;
    }

    const isValid = secureCompare(digest, hmacVal);
    if (!isValid) {
      throw new Error('Failed to validate Shopify request (2)');
    }
  };
}

export default Auth0IdentityProviderShopify;
